import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Upload, CheckCircle2, ArrowRight, Shield, CreditCard, QrCode } from 'lucide-react';
import { useLang } from '../i18n';
import { Card, Button, Badge, Field, Input, Select, Spinner, Stepper, Reveal } from '../components/ui';
import api, { vnd, fileToBase64 } from '../api';

const CLASSES = ['2-wheeler', '3-wheeler', 'passenger', 'commercial', 'special'];

export default function Buy() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [tab, setTab] = useState('lookup');
  const [reg, setReg] = useState('');
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState('');
  const [err, setErr] = useState('');
  const [vehicle, setVehicle] = useState(null);
  const [match, setMatch] = useState(null);
  const [selected, setSelected] = useState({});
  const [quote, setQuote] = useState(null);
  const [payMethod, setPayMethod] = useState('VNPay');
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    api.get('/config').then((r) => setDemos(r.data.demo_registrations)).catch(() => {});
  }, []);

  const doLookup = async (plate) => {
    setErr(''); setLoading('lookup');
    try {
      const r = await api.post('/vehicles/lookup', { registration_no: plate || reg });
      if (!r.data.found) { setErr(t('not_found')); setLoading(''); return; }
      setVehicle(r.data.vehicle); setLoading('');
    } catch (e) { setErr('Lookup failed'); setLoading(''); }
  };

  const doOcr = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setErr(''); setLoading('ocr');
    try {
      const b64 = await fileToBase64(file);
      const r = await api.post('/vehicles/ocr', { image_base64: b64 });
      const ex = r.data.extracted;
      setVehicle({
        registration_no: ex.registration_no || reg, vin: ex.vin, make: ex.make || '',
        model: ex.model || '', year: ex.year || 2020, vehicle_class: ex.vehicle_class || 'passenger',
        usage: 'personal', usage_subtype: 'non_commercial', seats: ex.seats || 5,
        load_capacity_kg: 0, estimated_value_vnd: 300000000, source: 'ocr',
      });
      setLoading('');
    } catch (e2) { setErr('OCR failed'); setLoading(''); }
  };

  const confirmVehicle = async () => {
    setLoading('confirm');
    try {
      const r = await api.post('/vehicles/confirm', vehicle);
      const v = r.data.vehicle; setVehicle(v);
      const m = await api.post('/insurance/match', { vehicle_id: v.id, language: lang });
      setMatch(m.data);
      const presel = {}; m.data.optional.forEach((p) => (presel[p.code] = false));
      setSelected(presel);
      setStep(1); setLoading('');
    } catch (e) { setErr('Could not match coverage'); setLoading(''); }
  };

  const buildQuote = async () => {
    setLoading('quote');
    try {
      const codes = [
        ...match.mandatory.map((p) => p.code),
        ...Object.keys(selected).filter((c) => selected[c]),
      ];
      const r = await api.post('/quotes', { vehicle_id: vehicle.id, product_codes: codes });
      setQuote(r.data); setStep(2); setLoading('');
    } catch (e) { setErr('Quote failed'); setLoading(''); }
  };

  const pay = async () => {
    setLoading('pay');
    try {
      const r = await api.post('/payment/simulate', { quote_id: quote.id, method: payMethod });
      setPolicy(r.data); setStep(3); setLoading('');
    } catch (e) { setErr('Payment failed'); setLoading(''); }
  };

  const steps = [t('step_vehicle'), t('step_coverage'), t('step_quote'), t('step_policy')];
  const name = (p) => (lang === 'vi' ? p.name_vi : p.name_en);
  const summary = (p) => (lang === 'vi' ? p.coverage_summary_vi : p.coverage_summary_en);

  return (
    <div className="max-w-4xl mx-auto px-5 py-12" data-testid="buy-page">
      <Reveal>
        <div className="kicker">{t('moment_buy')}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">{t('buy_h')}</h1>
        <p className="text-slate-400 mt-2">{t('buy_sub')}</p>
      </Reveal>

      <div className="mt-8"><Stepper steps={steps} current={step} /></div>
      {err && <div className="mb-4 text-crimson text-sm font-mono" data-testid="buy-error">{err}</div>}

      {/* STEP 0 — vehicle */}
      {step === 0 && (
        <Reveal>
          {!vehicle && (
            <Card className="p-6">
              <div className="flex gap-2 mb-5">
                {['lookup', 'scan'].map((tt) => (
                  <button key={tt} onClick={() => setTab(tt)} data-testid={`buy-tab-${tt}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${tab === tt ? 'bg-jade/10 text-jade border border-jade/40' : 'text-slate-400 border border-white/10'}`}>
                    {tt === 'lookup' ? t('tab_lookup') : t('tab_scan')}
                  </button>
                ))}
              </div>

              {tab === 'lookup' ? (
                <div>
                  <Field label={t('reg_label')}>
                    <div className="flex gap-2">
                      <Input data-testid="buy-registration-input" value={reg}
                        onChange={(e) => setReg(e.target.value)} placeholder={t('reg_ph')} />
                      <Button data-testid="buy-lookup-button" onClick={() => doLookup()} disabled={loading === 'lookup' || !reg}>
                        <Search className="w-4 h-4" /> {t('lookup_btn')}
                      </Button>
                    </div>
                  </Field>
                  {loading === 'lookup' && <div className="mt-3"><Spinner label="…" /></div>}
                  <div className="mt-5">
                    <span className="text-xs text-slate-500 font-mono">{t('try_demo')}</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {demos.map((d) => (
                        <button key={d} data-testid={`demo-plate-${d}`} onClick={() => { setReg(d); doLookup(d); }}
                          className="chip px-3 py-1.5 text-xs font-mono text-slate-300">{d}</button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-slate-400 mb-4">{t('scan_hint')}</p>
                  <label className="card-hover border-2 border-dashed border-white/15 rounded-2xl p-10 flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:text-jade hover:border-jade/40">
                    <Upload className="w-8 h-8 mb-3" />
                    <span className="text-sm">{t('upload_doc')}</span>
                    <input type="file" accept="image/*" className="hidden" data-testid="buy-ocr-upload" onChange={doOcr} />
                  </label>
                  {loading === 'ocr' && <div className="mt-4"><Spinner label={t('extracting')} /></div>}
                </div>
              )}
            </Card>
          )}

          {vehicle && (
            <Card className="p-6" data-testid="buy-confirm-vehicle">
              <div className="flex items-center gap-2 mb-5">
                <CheckCircle2 className="w-5 h-5 text-jade" />
                <h3 className="font-display text-xl font-bold">{t('is_this')}</h3>
                <Badge tone="slate" className="ml-auto">{vehicle.source}</Badge>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t('f_make')}><Input value={vehicle.make} onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })} data-testid="vehicle-make" /></Field>
                <Field label={t('f_model')}><Input value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} data-testid="vehicle-model" /></Field>
                <Field label={t('f_year')}><Input type="number" value={vehicle.year} onChange={(e) => setVehicle({ ...vehicle, year: +e.target.value })} /></Field>
                <Field label={t('f_class')}>
                  <Select value={vehicle.vehicle_class} onChange={(e) => setVehicle({ ...vehicle, vehicle_class: e.target.value })} data-testid="vehicle-class">
                    {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </Field>
                <Field label={t('f_usage')}>
                  <div className="flex gap-2">
                    {['personal', 'commercial'].map((u) => (
                      <button key={u} data-testid={`usage-${u}`} onClick={() => setVehicle({ ...vehicle, usage: u, usage_subtype: u === 'personal' ? 'non_commercial' : 'delivery' })}
                        className={`flex-1 py-3 rounded-xl text-sm border ${vehicle.usage === u ? 'bg-jade/10 text-jade border-jade/40' : 'text-slate-400 border-white/10'}`}>
                        {u === 'personal' ? t('usage_personal') : t('usage_commercial')}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label={t('f_seats')}><Input type="number" value={vehicle.seats} onChange={(e) => setVehicle({ ...vehicle, seats: +e.target.value })} /></Field>
                <Field label={t('f_value')}><Input type="number" value={vehicle.estimated_value_vnd} onChange={(e) => setVehicle({ ...vehicle, estimated_value_vnd: +e.target.value })} data-testid="vehicle-value" /></Field>
              </div>
              <div className="flex gap-2 mt-6">
                <Button variant="ghost" onClick={() => { setVehicle(null); setErr(''); }}>←</Button>
                <Button data-testid="buy-confirm-button" onClick={confirmVehicle} disabled={loading === 'confirm'} className="flex-1">
                  {loading === 'confirm' ? <Spinner label="…" /> : <>{t('confirm_btn')} <ArrowRight className="w-4 h-4" /></>}
                </Button>
              </div>
            </Card>
          )}
        </Reveal>
      )}

      {/* STEP 1 — coverage */}
      {step === 1 && match && (
        <Reveal>
          <div className="space-y-5" data-testid="buy-coverage">
            <Card className="p-5 border-jade/20">
              <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-jade" /><span className="kicker">{t('ai_guidance')}</span></div>
              <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-line" data-testid="ai-explanation">{match.ai_explanation}</p>
            </Card>

            <div>
              <div className="kicker mb-2">{t('mandatory')}</div>
              {match.mandatory.map((p) => (
                <Card key={p.code} className="p-4 mb-2 border-jade/30" data-testid={`product-${p.code}`}>
                  <div className="flex items-center justify-between">
                    <div><div className="font-semibold">{name(p)}</div><div className="text-xs text-slate-400 mt-1">{summary(p)}</div></div>
                    <Badge>{t('mandatory')}</Badge>
                  </div>
                </Card>
              ))}
            </div>

            {match.optional.length > 0 && (
              <div>
                <div className="kicker mb-2">{t('optional')}</div>
                {match.optional.map((p) => (
                  <button key={p.code} data-testid={`product-${p.code}`} onClick={() => setSelected({ ...selected, [p.code]: !selected[p.code] })}
                    className={`card w-full text-left p-4 mb-2 transition-colors ${selected[p.code] ? 'border-jade/50' : ''}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div><div className="font-semibold">{name(p)}</div><div className="text-xs text-slate-400 mt-1">{summary(p)}</div></div>
                      <div className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 ${selected[p.code] ? 'bg-jade border-jade' : 'border-white/20'}`}>
                        {selected[p.code] && <CheckCircle2 className="w-4 h-4 text-ink" />}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <Button data-testid="buy-see-quote-button" onClick={buildQuote} disabled={loading === 'quote'} className="w-full">
              {loading === 'quote' ? <Spinner label="…" /> : <>{t('see_quote')} <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </div>
        </Reveal>
      )}

      {/* STEP 2 — quote */}
      {step === 2 && quote && (
        <Reveal>
          <Card className="p-6" data-testid="buy-quote">
            <div className="kicker mb-4">{t('premium_breakdown')}</div>
            <div className="space-y-2">
              {quote.breakdown.map((b) => {
                const prod = quote.products.find((p) => p.code === b.code);
                return (
                  <div key={b.code} className="flex items-center justify-between py-2 border-b border-white/5">
                    <div><div className="text-sm">{prod ? name(prod) : b.code}</div><div className="text-xs text-slate-500">{b.detail}</div></div>
                    <div className="font-mono text-sm">{vnd(b.amount_vnd)}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/10">
              <span className="font-display text-lg font-bold">{t('total')}</span>
              <span className="font-display text-2xl font-extrabold text-jade" data-testid="quote-total">{vnd(quote.total_vnd)} <span className="text-sm text-slate-500 font-body font-normal">{t('per_year')}</span></span>
            </div>

            <div className="mt-6">
              <Field label={t('pay_method')}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {['VNPay', 'MoMo', 'Card', 'QR Pay'].map((m) => (
                    <button key={m} data-testid={`pay-method-${m}`} onClick={() => setPayMethod(m)}
                      className={`py-3 rounded-xl text-sm border flex items-center justify-center gap-2 ${payMethod === m ? 'bg-jade/10 text-jade border-jade/40' : 'text-slate-400 border-white/10'}`}>
                      {m === 'Card' ? <CreditCard className="w-4 h-4" /> : m === 'QR Pay' ? <QrCode className="w-4 h-4" /> : null} {m}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <Button data-testid="buy-pay-button" onClick={pay} disabled={loading === 'pay'} className="w-full mt-6">
              {loading === 'pay' ? <Spinner label={t('processing')} /> : <>{t('pay_now')} <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </Card>
        </Reveal>
      )}

      {/* STEP 3 — policy */}
      {step === 3 && policy && (
        <Reveal>
          <Card className="p-7 relative overflow-hidden" data-testid="buy-policy-issued">
            <div className="absolute top-0 right-0 w-40 h-40 bg-jade/10 blur-3xl rounded-full" />
            <div className="flex items-center gap-2 text-jade mb-5"><CheckCircle2 className="w-6 h-6" /><span className="font-display text-xl font-bold">{t('policy_issued')}</span></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><div className="kicker">{t('policy_no')}</div><div className="font-mono text-lg mt-1" data-testid="policy-number">{policy.policy_no}</div></div>
              <div><div className="kicker">{t('cert_no')}</div><div className="font-mono text-lg mt-1">{policy.certificate_no}</div></div>
              <div><div className="kicker">Vehicle</div><div className="mt-1">{policy.vehicle.make} {policy.vehicle.model} {policy.vehicle.year}</div></div>
              <div><div className="kicker">{t('valid_until')}</div><div className="mt-1">{policy.end_date.slice(0, 10)}</div></div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {policy.products.map((p) => <Badge key={p.code} tone="slate">{p.code}</Badge>)}
            </div>
            <Button data-testid="go-wallet-button" onClick={() => nav('/carry')} className="mt-6">
              {t('go_wallet')} <ArrowRight className="w-4 h-4" />
            </Button>
          </Card>
        </Reveal>
      )}
    </div>
  );
}
